import * as React from 'react'

import { MapPinIcon, Table } from 'lucide-react'
import { inject, observer } from 'mobx-react'
import { RouteComponentProps, withRouter } from 'react-router'

import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import useLocationResources from '~/client/src/shared/hooks/useLocationResources'
import { useLocationSubtitle } from '~/client/src/shared/hooks/useLocationSubtitle'
import { useResourceRuntimeContext } from '~/client/src/shared/hooks/useResourceRuntimeContext'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { buildResourceUiConfig } from '~/client/src/shared/resources/builders/buildResourceUiConfig'
import { renderResourceRecordIcon } from '~/client/src/shared/resources/icons/resourceRecordIcon'
import { renderResourceSemanticIcon } from '~/client/src/shared/resources/icons/resourceSemanticIcons'

import { MobileResourceConfig } from '../../components/MobileResource'
import MobileResourceViewLayout from '../../components/MobileResource/MobileResourceViewLayout'
import MobileInitialState from '../../stores/MobileInitialState'
import MobileCommonStore from '../../stores/ui/MobileCommon.store'
import MobileLocationsEditPanel from './MobileLocationsEditPanel'

interface IProps {
  state?: MobileInitialState
  common?: MobileCommonStore
}

const LOCATIONS_UI_DEFAULTS = buildResourceUiConfig(ResourceTypeId.LOCATIONS)

// The title passes size="lg", a named token Lucide can't resolve.
const HeaderIcon: React.FC<{ className?: string }> = ({ className }) => (
  <MapPinIcon size={28} className={className} />
)

const MobileLocationsView: React.FC<IProps & RouteComponentProps> = ({
  state,
  common,
}) => {
  const projectId = state?.activeProject?.id
  const runtimeContext = useResourceRuntimeContext()

  const resolveSubtitle = useLocationSubtitle(ResourceTypeId.LOCATIONS)

  const resourceConfig = React.useMemo<MobileResourceConfig>(
    () => ({
      resourceTypeId: ResourceTypeId.LOCATIONS,
      title: Localization.translator.locations,
      icon: HeaderIcon,
      useRecords: useLocationResources,
      initialSortKey: LOCATIONS_UI_DEFAULTS.defaultSortKey,
      initialSortOrder: LOCATIONS_UI_DEFAULTS.defaultSortOrder,
      initialGroupByKey: LOCATIONS_UI_DEFAULTS.defaultGroupingKey,
      countLabel: count => `${count} ${Localization.translator.locations}`,
      // List only, matching Equipment and Materials. Locations are spatial and the desktop app
      // draws them on a map, but the only mobile resource app with a map is StruxTags, whose
      // map is its own component - there is no shared mobile map surface to opt into yet.
      viewModes: [
        { mode: 'list', Icon: Table, label: Localization.translator.listView },
      ],
      noneGroupByValue: LOCATIONS_UI_DEFAULTS.noneGroupingKey,
      emptyMessage: Localization.translator.noDataAvailable,
      // Every location carries its own glyph and colour (iconName/color are root fields
      // on this type), so the row shows the location rather than a generic pin.
      resolveSubtitle: ({ record, subtitle }) =>
        resolveSubtitle(record, subtitle),
      resolveLeadingSlot: record =>
        renderResourceRecordIcon(
          ResourceTypeId.LOCATIONS,
          record,
          renderResourceSemanticIcon(
            'location',
            'tw:block tw:size-full tw:text-muted-foreground',
          ),
          'tw:block tw:size-full tw:text-muted-foreground',
        ),
      runtimeContext,
    }),
    [runtimeContext, resolveSubtitle],
  )

  React.useEffect(() => {
    common.hideNavBar()
  }, [common])

  return (
    <MobileResourceViewLayout
      config={resourceConfig}
      state={state}
      projectId={projectId}
    >
      <MobileLocationsEditPanel />
    </MobileResourceViewLayout>
  )
}

export default withRouter(
  inject('state', 'common')(observer(MobileLocationsView)),
)
